// src/crm/UnmatchedEmailsPage.tsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { Creator, UnmatchedEmail } from '../lib/types'
import { useUnmatchedEmails, useAssignUnmatchedEmail } from './hooks/useUnmatchedEmails'

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function AssignModal({
  email,
  onClose,
}: {
  email: UnmatchedEmail
  onClose: () => void
}) {
  const [creatorId, setCreatorId] = useState('')
  const assign = useAssignUnmatchedEmail()

  const { data: creators = [] } = useQuery<Creator[]>({
    queryKey: ['creators'],
    queryFn: () =>
      api.get<{ creators: Creator[] }>('/api/creators').then((r) => r.creators),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!creatorId) return
    assign.mutate(
      { unmatchedId: email.id, creatorId },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
          padding: '1.5rem',
          minWidth: '380px',
          maxWidth: '480px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'var(--fg)',
            margin: '0 0 0.25rem',
          }}
        >
          Rattacher à un créateur
        </h3>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--muted)',
            margin: '0 0 1rem',
          }}
        >
          De : <strong style={{ color: 'var(--fg)' }}>{email.fromAddr}</strong>
          {email.subject && (
            <>
              {' '}— {email.subject}
            </>
          )}
        </p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.8125rem',
                color: 'var(--muted)',
                marginBottom: '0.375rem',
              }}
            >
              Créateur
            </label>
            <select
              value={creatorId}
              onChange={(e) => setCreatorId(e.target.value)}
              required
              style={{
                width: '100%',
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: 'var(--fg)',
                fontSize: '0.875rem',
                padding: '0.5rem 0.75rem',
                fontFamily: 'inherit',
              }}
            >
              <option value="">— Sélectionner un créateur —</option>
              {creators.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                  {c.email ? ` (${c.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: 'var(--muted)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!creatorId || assign.isPending}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--arancio)',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: creatorId && !assign.isPending ? 'pointer' : 'not-allowed',
                opacity: creatorId && !assign.isPending ? 1 : 0.6,
                fontFamily: 'inherit',
              }}
            >
              {assign.isPending ? 'Rattachement…' : 'Rattacher'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function UnmatchedEmailsPage() {
  const { data: emails = [], isLoading } = useUnmatchedEmails()
  const [selectedEmail, setSelectedEmail] = useState<UnmatchedEmail | null>(null)

  if (isLoading) {
    return (
      <div style={{ padding: '2rem 2.5rem', color: 'var(--muted)' }}>Chargement…</div>
    )
  }

  return (
    <div style={{ padding: '2rem 2.5rem', maxWidth: '860px' }}>
      <h1
        style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--fg)',
          letterSpacing: '-0.025em',
          marginBottom: '0.375rem',
        }}
      >
        Emails non assignés
      </h1>
      <p
        style={{
          fontSize: '0.875rem',
          color: 'var(--muted)',
          marginBottom: '1.5rem',
          marginTop: 0,
        }}
      >
        Emails entrants dont l'expéditeur n'a pas été reconnu automatiquement.
      </p>

      {emails.length === 0 ? (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--dim)',
            fontSize: '0.875rem',
          }}
        >
          Aucun email en attente d'assignation.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {emails.map((email) => (
            <div
              key={email.id}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: '8px',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--fg)',
                    marginBottom: '0.125rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {email.subject ?? '(sans sujet)'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    fontSize: '0.8125rem',
                    color: 'var(--muted)',
                  }}
                >
                  <span>{email.fromAddr}</span>
                  <span style={{ color: 'var(--dim)' }}>{formatDate(email.receivedAt)}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedEmail(email)}
                style={{
                  flexShrink: 0,
                  padding: '0.375rem 0.875rem',
                  background: 'transparent',
                  border: '1px solid var(--line)',
                  borderRadius: '6px',
                  color: 'var(--bronze)',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                Rattacher
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedEmail !== null && (
        <AssignModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  )
}
